import { HardHat, Scale, Stethoscope, UtensilsCrossed, Wrench, Home } from "lucide-react";

export const INDUSTRIES = [
  {
    slug: "contractors",
    name: "Contractors & Construction",
    short: "Contractors",
    Icon: HardHat,
    metaTitle: "Website Design for Contractors | Inland Empire & Orange County — Redkey",
    metaDescription:
      "Custom websites and local SEO for general contractors, roofers, remodelers, and builders in the Inland Empire and Orange County. More quote requests, fewer tire-kickers.",
    headline: "Contractor websites that bring in qualified quote requests, not tire-kickers.",
    intro:
      "Homeowners and property managers hire contractors they can trust. Your website should prove that in seconds — real project photos, license and insurance details, clear service areas, and a quote form that asks the right questions before you ever pick up the phone.",
    problems: [
      "Template sites that look the same as every competitor",
      "Quote forms that attract price shoppers outside your service area",
      "No service-area pages, so you only rank in one city",
      "Project galleries that slow the site to a crawl on mobile",
    ],
    features: [
      "Project portfolio with fast, optimized photography",
      "Service pages for every trade you offer",
      "Service-area pages for the cities you actually work in",
      "Quote form that asks budget, timeline, and location",
      "License, insurance, and review trust signals",
      "Click-to-call and text buttons on mobile",
    ],
    faq: [
      { q: "Can my contractor website filter out low-budget leads?", a: "Yes. We build quote forms that ask project type, budget range, timeline, and location, then highlight the best-fit requests so you call those first." },
      { q: "Do you help with Google Business Profile for contractors?", a: "Yes. We align your website, service areas, and Google Business Profile so they reinforce each other in local search." },
    ],
    relatedWorkIds: ["aaa-tree"],
  },
  {
    slug: "law-firms",
    name: "Law Firms & Attorneys",
    short: "Law Firms",
    Icon: Scale,
    metaTitle: "Law Firm Website Design | Riverside & Orange County Attorneys — Redkey",
    metaDescription:
      "Custom, ADA-compliant law firm websites for attorneys in Riverside, San Bernardino, Santa Ana, Irvine, and across Southern California. Practice-area SEO and qualified case intake.",
    headline: "Law firm websites that earn trust and qualify cases before the first call.",
    intro:
      "Legal search results are some of the most competitive on Google. A credible, fast, accessible website with clear practice-area pages and a smart intake form is how a firm stands out without paying a premium for every click.",
    problems: [
      "Generic legal templates that make every firm look the same",
      "Practice-area pages too thin to rank",
      "Intake forms that collect unqualified or out-of-state inquiries",
      "Accessibility gaps that create ADA exposure for the firm itself",
    ],
    features: [
      "In-depth practice-area pages written for search intent",
      "Attorney bios with credentials and structured data",
      "Case-type intake forms with qualifying questions",
      "WCAG 2.2 AA accessibility throughout",
      "Bilingual-ready structure for English and Spanish",
      "Fast, secure hosting with modern security headers",
    ],
    faq: [
      { q: "Do you write content for law firm websites?", a: "We plan the page structure and search targets and help shape copy; your attorneys review everything for accuracy and bar-rule compliance." },
      { q: "Can the intake form route different case types?", a: "Yes. We can route inquiries by practice area, location, or urgency so the right person follows up quickly." },
    ],
    relatedWorkIds: ["city-of-riverside"],
  },
  {
    slug: "dental-medical",
    name: "Dental & Medical Practices",
    short: "Dental & Medical",
    Icon: Stethoscope,
    metaTitle: "Dental & Medical Website Design | Inland Empire & OC Practices — Redkey",
    metaDescription:
      "Accessible, patient-friendly websites for dentists, medical offices, and wellness practices in the Inland Empire and Orange County. More appointment requests, built to WCAG 2.2 AA.",
    headline: "Practice websites that patients trust — and that book appointments.",
    intro:
      "Patients compare practices online before they call. A calm, credible website with clear services, insurance information, real photos, and one-click booking turns that research into appointments — and accessibility is not optional when your audience includes every patient.",
    problems: [
      "Stock-photo sites that feel impersonal",
      "Booking buried three clicks deep",
      "Insurance and pricing questions left unanswered",
      "Accessibility issues that exclude patients and invite ADA claims",
    ],
    features: [
      "Service and treatment pages patients understand",
      "Online booking integration on every page",
      "Insurance, financing, and new-patient information",
      "WCAG 2.2 AA accessibility, screen-reader tested",
      "Forms that avoid collecting protected health information",
      "Local SEO for each office location",
    ],
    faq: [
      { q: "Is my practice website HIPAA compliant?", a: "We keep protected health information out of standard website forms and integrate with your HIPAA-compliant scheduling or intake platform for anything sensitive." },
      { q: "Can you help us get more new patients from Google?", a: "Yes. Treatment pages, location pages, structured data, and review strategy all work together to improve local visibility." },
    ],
    relatedWorkIds: ["amber-rdh"],
  },
  {
    slug: "restaurants",
    name: "Restaurants & Hospitality",
    short: "Restaurants",
    Icon: UtensilsCrossed,
    metaTitle: "Restaurant Website Design | Inland Empire & Orange County — Redkey",
    metaDescription:
      "Fast, beautiful restaurant, winery, and hospitality websites for the Inland Empire and Orange County. Easy menus, reservations, catering inquiries, and local SEO.",
    headline: "Restaurant and hospitality websites that load fast and fill tables.",
    intro:
      "Diners decide in seconds, usually on a phone. Your website needs a menu that loads instantly, reservations one tap away, current hours, and photography that sells the experience without dragging the page down.",
    problems: [
      "PDF menus that are slow and unreadable on phones",
      "Outdated hours and specials",
      "Reservation and catering requests scattered across channels",
      "Heavy images that hurt Google rankings",
    ],
    features: [
      "Mobile-first HTML menus you can update yourself",
      "Reservation and online ordering integrations",
      "Catering and private event inquiry forms",
      "Optimized photography that still loads fast",
      "Restaurant structured data for Google",
      "Events and specials that are easy to keep current",
    ],
    faq: [
      { q: "Can I update the menu myself?", a: "Yes. Menus, hours, and specials are editable in a simple admin — no developer needed." },
      { q: "Can you capture catering and event leads?", a: "Yes. We build inquiry forms that ask date, guest count, and budget so you can prioritize the best events." },
    ],
    relatedWorkIds: ["festival-of-lights", "golden-candids"],
  },
  {
    slug: "home-services",
    name: "Home Services",
    short: "Home Services",
    Icon: Wrench,
    metaTitle: "Home Service Website Design | HVAC, Plumbing, Tree Service — Redkey",
    metaDescription:
      "Lead-generating websites for HVAC, plumbing, electrical, landscaping, tree service, and pest control companies in the Inland Empire and Orange County.",
    headline: "Home service websites built to make the phone ring.",
    intro:
      "When a homeowner needs a plumber, an HVAC tech, or a tree crew, they search, compare two or three sites, and call. Our home-service websites are built to win that comparison: fast, local, trustworthy, and impossible to leave without knowing how to reach you.",
    problems: [
      "Competitors with worse work outranking you",
      "Expensive lead services eating your margins",
      "No pages for the cities and services you want more of",
      "Mobile visitors who cannot find the phone number",
    ],
    features: [
      "Sticky click-to-call on mobile",
      "Service pages for each job type you want more of",
      "City service-area pages across your territory",
      "Quote and booking forms above the fold",
      "Review and guarantee trust signals",
      "Local SEO and Google Business Profile alignment",
    ],
    faq: [
      { q: "Can a website replace paid lead services?", a: "For many home-service businesses, a well-built site with local SEO becomes the main source of jobs. AAA Tree Service is a real example from our portfolio." },
      { q: "Do you offer monthly SEO for home-service businesses?", a: "Yes. Retainers cover new service pages, content, technical SEO, and Google Business Profile updates." },
    ],
    relatedWorkIds: ["aaa-tree"],
  },
  {
    slug: "real-estate",
    name: "Real Estate & Property",
    short: "Real Estate",
    Icon: Home,
    metaTitle: "Real Estate Website Design | Inland Empire & Orange County — Redkey",
    metaDescription:
      "Custom websites for real estate agents, brokerages, and property managers in the Inland Empire and Orange County. Neighborhood pages, listing integrations, and qualified buyer and seller leads.",
    headline: "Real estate websites that position you as the local expert.",
    intro:
      "Buyers and sellers choose agents and property managers who clearly know the neighborhood. A custom website with real neighborhood content, listing integration, and smart lead capture builds that authority in a way portal profiles never will.",
    problems: [
      "Brokerage template sites that look like everyone else's",
      "Lead forms that capture curiosity, not intent",
      "No neighborhood content to rank for local searches",
      "Slow listing pages on mobile",
    ],
    features: [
      "Neighborhood and community guide pages",
      "IDX or listing feed integration",
      "Buyer and seller lead forms with timeline and price range",
      "Home valuation request flows",
      "Agent and team profile pages",
      "Fast, accessible, mobile-first design",
    ],
    faq: [
      { q: "Can you integrate MLS listings?", a: "Yes. We integrate with common IDX providers and style listings to match your brand." },
      { q: "Do you build websites for property management companies?", a: "Yes. We build owner and tenant paths, service-area pages, and inquiry forms that qualify property owners by portfolio size and location." },
    ],
    relatedWorkIds: ["golden-candids"],
  },
];

export const getIndustry = (slug) => INDUSTRIES.find((i) => i.slug === slug);
